<template>
  <Card class="bulk-tender-card no-pad">
    <template #content>
      <div class="bulk-tender-card-carrier">
        <CarrierAvatar
          :carrier="internalCarrier"
          :accent-color="accentColor"
          size="large"
        />

        <div class="carrier-name-container flex flex-column">
          <p class="bulk-tender-card-carrier-name">
            {{ carrierName }}
            {{ scac ? `(${scac})` : '' }}
          </p>
        </div>

        <RygenChip
          v-if="isDoNotDispatchEnabled && isDoNotDispatch"
          class="carrier-do-not-dispatch-warning"
          :label="t('carriers.doNotDispatch')"
          :severity="
            canOverrideCarrierDoNotDispatch ? Severity.WARNING : Severity.DANGER
          "
        />
      </div>

      <div class="bulk-tender-card-highlights flex flex-column flex-gap-2">
        <KeyValuePair
          :label="t('shipments.shipments', 2)"
          :value="numberOfShipments"
          uppercase-label
        />

        <div class="tender-config-options flex flex-gap-1">
          <InputWrapper
            v-slot="{ ariaProps, inputId }"
            :input-id="`bulk-tender-card-${scac}-methods`"
            class="w-6"
            :label="t('shipments.tenderMethods')"
          >
            <RygenDropdown
              :id="inputId"
              v-model="internalTenderMethod"
              v-bind="ariaProps"
              :options="availableTenderMethods"
              class="flex-grow-1"
              append-to="body"
              :disabled="availableTenderMethods.length === 1"
              :placeholder="t('shipments.tenderMethods')"
            />
          </InputWrapper>

          <InputWrapper
            v-if="internalTenderMethod === TenderMethod.EMAIL"
            v-slot="{ ariaProps, errors, inputClasses, inputId, invalid }"
            :input-id="`bulk-tender-card-${scac}-emails`"
            class="w-6"
            :errors="v$.emails.$errors"
            :label="t('shipments.emailsCommaSeparated')"
          >
            <InputText
              :id="inputId"
              v-model="internalTenderConfigOptions.emails"
              v-bind="ariaProps"
              :class="[
                inputClasses,
                {
                  'input-warning':
                    !errors.length &&
                    !internalTenderConfigOptions?.emails?.length,
                },
              ]"
              :invalid="invalid"
              :placeholder="t('shipments.emailsCommaSeparated')"
              :disabled="emailDisabled"
            />
          </InputWrapper>
        </div>
        <p
          v-if="internalTenderMethod === TenderMethod.MANUAL"
          class="tender-method-message"
        >
          {{ t('shipments.confirmTenderManual') }}
        </p>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import isEqual from 'lodash/isEqual';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import {
  CarrierSetting,
  Loadable,
  Severity,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { useCorsairPermissions, useDoNotDispatch } from '@/composables';
import { Carrier } from '@/model';
import { useCarrierStore } from '@/stores/Carrier';
import { useCurrentUserStore } from '@/stores/CurrentUser';

import CarrierAvatar from '@rygen/rygen-ui/components/carrier/CarrierAvatar.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';

const props = defineProps({
  numberOfShipments: {
    type: Number,
    required: true,
  },
  carrierName: {
    type: String,
    default: '',
  },
  scac: {
    type: String,
    default: '',
  },
  tenderMethod: {
    type: String as PropType<TenderMethod>,
    default: () => undefined,
  },
  tenderConfigOptions: {
    type: TenderConfigOptions,
    default: () => undefined,
  },
  carrierSettings: {
    type: Object as PropType<CarrierSetting>,
    required: true,
  },
  availableTenderMethods: {
    type: Array as PropType<TenderMethod[]>,
    required: true,
  },
});

const {
  numberOfShipments,
  carrierName,
  scac,
  tenderMethod,
  tenderConfigOptions,
  carrierSettings,
  availableTenderMethods,
} = toRefs(props);

const emit = defineEmits<{
  (event: 'update:tender-method', tenderMethod: TenderMethod): void;
  (
    event: 'update:tender-config-options',
    tenderConfigOptions: TenderConfigOptions,
  ): void;
}>();

const { accentColor } = useCurrentUserStore();
const { t } = useI18n();
const { shouldDisableTenderEmail } = useCorsairPermissions();
const { canOverrideCarrierDoNotDispatch, isDoNotDispatchEnabled } =
  useDoNotDispatch();
const { loadCarrierByScac } = useCarrierStore();

const emailDisabled = computed((): boolean => {
  return shouldDisableTenderEmail(carrierSettings.value.carrier?.type);
});

const internalTenderMethod = ref(tenderMethod?.value);

const internalTenderConfigOptions = ref(
  props.tenderConfigOptions ?? new TenderConfigOptions(),
);

const carrierLoadable: Ref<Loadable<Carrier>> = ref({ loading: false });

const rules = computed(() => {
  const r: Record<string, Record<string, ValidationRule>> = {
    emails: {},
  };

  if (internalTenderMethod.value === TenderMethod.EMAIL) {
    r.emails.required = required(t('common.email'));
  }

  return r;
});

const { v$ } = useValidations(internalTenderConfigOptions, rules);

const isDoNotDispatch = computed(() => {
  return carrierSettings.value.doNotDispatch;
});

const internalCarrier = computed(() => {
  return new Carrier({
    name: carrierName.value,
    scac: scac.value,
    ...carrierSettings.value.carrier,
    ...carrierLoadable.value.data,
  });
});

watch(
  () => tenderConfigOptions?.value,
  (newVal: TenderConfigOptions | undefined) => {
    internalTenderConfigOptions.value = new TenderConfigOptions(newVal);
  },
);

watch(internalTenderConfigOptions, (newVal: TenderConfigOptions) => {
  if (tenderConfigOptions && isEqual(tenderConfigOptions.value, newVal)) {
    return;
  }

  emit('update:tender-config-options', newVal);
});

watch(
  () => tenderMethod?.value,
  (newVal: TenderMethod | undefined) => {
    internalTenderMethod.value = newVal;
  },
);

watch(internalTenderMethod, (newVal: TenderMethod | undefined) => {
  if (newVal === undefined) {
    return;
  }

  const tenderConfig = carrierSettings.value.tenderConfig.find(
    (config) => config.method === newVal,
  );

  if (tenderConfig?.configOptions) {
    internalTenderConfigOptions.value = new TenderConfigOptions(
      tenderConfig.configOptions,
    );
  }

  emit('update:tender-method', newVal);
});

watch(
  scac,
  async (newScacVal) => {
    if (newScacVal) {
      carrierLoadable.value = loadCarrierByScac(newScacVal);
    } else {
      carrierLoadable.value = { loading: false };
    }
  },
  { immediate: true },
);
</script>

<style>
.bulk-tender-card.p-card {
  container: bulk-tender-card / inline-size;

  .p-card-content {
    display: flex;
    gap: var(--gap-1);
    justify-content: space-between;
    align-items: center;
    padding: var(--sp-3);

    .bulk-tender-card-carrier {
      display: grid;
      gap: var(--sp-1) var(--gap-1);
      grid-template-columns: auto 1fr;
      grid-template-areas:
        'avatar carrier-name'
        'blank warnings';
      justify-content: start;
      align-items: center;
      align-content: center;
      flex: 1 0 33%;
      border-right: var(--divider);
      align-self: stretch;
      padding: 0 var(--sp-6) 0 0;

      .carrier-name-container {
        .bulk-tender-card-carrier-name {
          font-size: 16px;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }
      }

      .carrier-do-not-dispatch-warning {
        grid-area: warnings;
        white-space: nowrap;
        width: fit-content;
        margin-top: var(--sp-1);
      }
    }

    .bulk-tender-card-highlights {
      flex: 1 0 66%;
      width: 100%;

      .tender-method-message {
        font-size: 14px;
        color: var(--p-gray-400);
      }

      .bulk-tender-card-highlights-cell {
        display: flex;
        align-items: center;
        flex: 1 0 auto;
        width: 25%;

        /*
        flex gap on the container would be better, but for some reason it makes
        the rate cell a different size than the other 3 cells.
      */
        &:not(:first-of-type) {
          padding-left: var(--sp-3);
        }

        &:not(:last-of-type) {
          padding-right: var(--sp-3);
        }
      }
    }
  }
}

@container bulk-tender-card (max-width: 800px) {
  .bulk-tender-card.p-card {
    .p-card-content {
      flex-wrap: wrap;
      row-gap: var(--gap-2);

      .bulk-tender-card-carrier {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--gap-1);
        border-right: none;
        border-bottom: var(--divider);
        padding: 0 0 var(--sp-3) 0;

        .carrier-name-container {
          text-align: center;
        }

        .carrier-do-not-dispatch-warning {
          min-width: unset;
          max-width: unset;
        }
      }
    }
  }
}
</style>
